import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import { MapPin, Phone, Mail, Send, CheckCircle2 } from "lucide-react";
import { COMPANY } from "@/lib/data";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  postcode: "",
  interest: "photovoltaique",
  profile: "particulier",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Merci de renseigner nom, email et message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote-requests`, form);
      setDone(true);
      toast.success("Merci ! Nous vous rappelons sous 24h ouvrées.");
      setForm(initial);
    } catch (err) {
      toast.error("Une erreur s'est produite. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-20 lg:py-32 bg-[#f5f1ea] border-t border-[#e2d9ce]"
      data-testid="contact-section"
    >
      <Toaster position="top-center" richColors />
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="eyebrow mb-4">Contact · Devis gratuit</div>
          <h2 className="font-serif-display font-medium text-[36px] sm:text-[48px] lg:text-[64px] text-[#0F172A] leading-[1] tracking-tight balance">
            Parlons de votre projet.
          </h2>
          <p className="mt-6 text-[16px] leading-relaxed text-[#334155] max-w-md">
            Étude technique offerte, chiffrage détaillé sous 48h. Aucun
            engagement — juste un accompagnement sérieux.
          </p>

          <div className="mt-10 space-y-4">
            <Info icon={<Phone className="h-4 w-4" />} label="Téléphone" value={COMPANY.phone} href={`tel:${COMPANY.phoneRaw}`} testid="contact-info-phone" />
            <Info icon={<Mail className="h-4 w-4" />} label="Email" value={COMPANY.email} href={`mailto:${COMPANY.email}`} testid="contact-info-email" />
            <Info
              icon={<MapPin className="h-4 w-4" />}
              label="Bureau"
              value={
                <>
                  {COMPANY.address}
                  <br />
                  {COMPANY.city}
                </>
              }
              testid="contact-info-address"
            />
          </div>

          <div className="mt-10 flex flex-wrap gap-2">
            {["RGE QualiPV", "Qualibat 7141", "MaPrimeRénov'"].map((b) => (
              <span key={b} className="text-[10px] font-mono-tech uppercase tracking-[0.2em] text-[#2D7A4D] bg-[#e6f4ea] border border-[#c8e6d0] px-2.5 py-1.5 rounded-full">
                {b}
              </span>
            ))}
          </div>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          onSubmit={submit}
          className="lg:col-span-7 bg-[#fdfbf7] border border-[#e2d9ce] rounded-2xl p-6 lg:p-10"
          data-testid="contact-form"
        >
          {done ? (
            <div className="min-h-[400px] flex flex-col items-center justify-center text-center px-6" data-testid="contact-success">
              <div className="h-16 w-16 rounded-full bg-[#e6f4ea] flex items-center justify-center mb-6">
                <CheckCircle2 className="h-8 w-8 text-[#2D7A4D]" />
              </div>
              <h3 className="font-serif-display text-3xl text-[#0F172A]">Demande envoyée</h3>
              <p className="mt-3 text-[#334155] max-w-md">
                Un conseiller HMCD vous recontacte sous 24 h ouvrées pour
                planifier une visite technique gratuite.
              </p>
              <button
                data-testid="contact-new-request"
                onClick={() => setDone(false)}
                className="mt-8 btn-ghost text-sm"
              >
                Envoyer une autre demande
              </button>
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Nom complet *" value={form.name} onChange={update("name")} testid="input-name" />
                <Field label="Email *" type="email" value={form.email} onChange={update("email")} testid="input-email" />
                <Field label="Téléphone" value={form.phone} onChange={update("phone")} testid="input-phone" />
                <Field label="Code postal" value={form.postcode} onChange={update("postcode")} testid="input-postcode" />
              </div>

              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <Select
                  label="Vous êtes"
                  value={form.profile}
                  onChange={update("profile")}
                  options={[
                    { v: "particulier", l: "Particulier" },
                    { v: "professionnel", l: "Professionnel / Collectivité" },
                  ]}
                  testid="input-profile"
                />
                <Select
                  label="Intéressé par"
                  value={form.interest}
                  onChange={update("interest")}
                  options={[
                    { v: "photovoltaique", l: "Panneaux photovoltaïques" },
                    { v: "ite", l: "Isolation extérieure (ITE)" },
                    { v: "pac", l: "Pompe à chaleur" },
                    { v: "batterie", l: "Stockage / batterie" },
                    { v: "global", l: "Projet global" },
                  ]}
                  testid="input-interest"
                />
              </div>

              <div className="mt-6">
                <label className="block font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#64748B] mb-2">
                  Votre projet *
                </label>
                <textarea
                  data-testid="input-message"
                  value={form.message}
                  onChange={update("message")}
                  rows={5}
                  placeholder="Type de logement, surface, année de construction, contraintes particulières…"
                  className="w-full bg-[#fdfbf7] border border-[#e2d9ce] rounded-lg px-4 py-3 text-[15px] text-[#0F172A] placeholder:text-[#94a3b8] focus:outline-none focus:border-[#0F172A] transition-colors"
                />
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                <p className="text-xs text-[#64748B] max-w-sm">
                  En envoyant ce formulaire, vous acceptez d'être recontacté par HMCD. Aucune donnée transmise à des tiers.
                </p>
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={submitting}
                  className="btn-accent disabled:opacity-60"
                >
                  {submitting ? "Envoi…" : "Envoyer ma demande"}
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </>
          )}
        </motion.form>
      </div>
    </section>
  );
}

function Info({ icon, label, value, href, testid }) {
  const inner = (
    <div className="flex items-start gap-4">
      <div className="h-10 w-10 rounded-full bg-[#fdfbf7] border border-[#e2d9ce] flex items-center justify-center text-[#2D7A4D] shrink-0">
        {icon}
      </div>
      <div>
        <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#64748B]">
          {label}
        </div>
        <div className="text-[15px] text-[#0F172A] mt-1 leading-snug">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a data-testid={testid} href={href} className="block hover:opacity-80 transition-opacity">
      {inner}
    </a>
  ) : (
    <div data-testid={testid}>{inner}</div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="block font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#64748B] mb-2">
        {label}
      </span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={onChange}
        className="w-full bg-[#fdfbf7] border border-[#e2d9ce] rounded-lg px-4 py-3 text-[15px] text-[#0F172A] focus:outline-none focus:border-[#0F172A] transition-colors"
      />
    </label>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="block font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#64748B] mb-2">
        {label}
      </span>
      <select
        data-testid={testid}
        value={value}
        onChange={onChange}
        className="w-full bg-[#fdfbf7] border border-[#e2d9ce] rounded-lg px-4 py-3 text-[15px] text-[#0F172A] focus:outline-none focus:border-[#0F172A] transition-colors appearance-none cursor-pointer"
      >
        {options.map((o) => (
          <option key={o.v} value={o.v}>
            {o.l}
          </option>
        ))}
      </select>
    </label>
  );
}
